import { css } from "@emotion/react";
import {
  CalendarTodayTwoTone,
  DashboardTwoTone,
  GroupTwoTone,
  ListAltTwoTone,
  LocalPhoneOutlined,
  MailOutline,
  PieChartTwoTone,
  PollTwoTone,
  RecentActorsTwoTone,
} from "@mui/icons-material";
import Link from "next/link";
import { useRouter } from "next/router";
import React from "react";
import { colorVars } from "../../utils/colorUtils";
import { useRecoilValue } from "recoil";
import { walkthroughAtom } from "../../atoms/walkthrough.atom";
import {
  hasCalendarPermissionsSelector,
  hasCommunityPermissionsSelector,
  hasInsightsPermissionsSelector,
  hasSurveysPermissionsSelector,
  hasTeamPermissionsSelector,
  isAdminSelector,
} from "../../atoms/user.atom";

type SidebarItem = {
  name: string;
  href: string;
  hidden?: boolean;
  icon: React.ReactElement;
  target: string;
  beta?: boolean;
};

const s = {
  container: css({
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    padding: "3.8rem 3.2rem",
    borderRadius: "5rem",
    backgroundColor: "white",
    width: "26.2rem",
    top: "2.1rem",
    position: "fixed",
    zIndex: 25,
    left: "3.1rem",
    height: "calc(100vh - 4.2rem)",
  }),
  top: css({ display: "flex", flexDirection: "column", rowGap: "4.4rem" }),
  button: css({
    display: "flex",
    columnGap: "1.2rem",
    alignItems: "center",
    textAlign: "start",
    backgroundColor: "white",
    borderRadius: ".8rem",
    cursor: "pointer",
    border: "none",
    outline: "none",
    padding: "1.6rem 1.2rem",
    ":hover": {
      fontWeight: 600,
    },
    ":focus": {
      backgroundColor: colorVars.bluePrimary,
      color: "white",
      opacity: 0.8,
      p: {
        color: "white",
      },
    },
  }),
  active: css({
    backgroundColor: colorVars.bluePrimary,
    color: "white",
    p: {
      color: "white",
    },
  }),
  beta: css({
    fontSize: "1.2rem",
    color: "white",
    backgroundColor: colorVars.bluePrimary,
    borderRadius: ".8rem",
    lineHeight: "2.2rem",
    padding: ".1rem .8rem",
  }),
  betaActive: css({
    backgroundColor: "white",
    color: `${colorVars.bluePrimary} !important`,
  }),
  resources: css({
    backgroundColor: "rgba(160, 208, 255, 0.15)",
    borderRadius: "2rem",
    padding: "3.6rem 3rem 2rem 2rem",
    display: "flex",
    flexDirection: "column",
    rowGap: "2.4rem",
    position: "relative",
  }),
  resourcesActive: css({
    backgroundColor: colorVars.bluePrimary,
    color: "white",
  }),
  headphones: css({
    width: "4.6rem",
    position: "absolute",
    right: "1rem",
    top: "1rem",
  }),
  resourceHeader: css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    rowGap: "1.6rem",
  }),
  resourceTitle: css({
    fontSize: "1.8rem",
    fontWeight: 600,
  }),
  resourceButton: css({
    border: "none",
    backgroundColor: colorVars.bluePrimary,
    borderRadius: "2rem",
    padding: ".6rem 1.6rem",
    color: "white",
    fontSize: "1.4rem",
    cursor: "pointer",
    ":hover": {
      fontWeight: 600,
    },
  }),
  resourceButtonActive: css({
    color: colorVars.bluePrimary,
    backgroundColor: "white",
  }),
  resourceLinks: css({
    display: "flex",
    flexDirection: "column",
    rowGap: ".6rem",
  }),
  link: css({
    color: colorVars.bluePrimary,
    textDecoration: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    columnGap: ".6rem",
    p: {
      color: colorVars.bluePrimary,
      fontSize: "1.2rem",
    },
    ":hover": {
      fontWeight: 700,
    },
  }),
  linkActive: css({
    color: "white",
    p: {
      color: "white",
    },
  }),
};

type SideBarNavProps = {
  disabled?: boolean;
};
const SideBarNav = ({ disabled }: SideBarNavProps) => {
  const isAdmin = useRecoilValue(isAdminSelector);
  const hasTeamPermission = useRecoilValue(hasTeamPermissionsSelector);
  const hasSurveysPermission = useRecoilValue(hasSurveysPermissionsSelector);
  const hasCommunityPermission = useRecoilValue(
    hasCommunityPermissionsSelector
  );
  const hasCalendarPermission = useRecoilValue(hasCalendarPermissionsSelector);
  const hasInsightsPermission = useRecoilValue(hasInsightsPermissionsSelector);
  const router = useRouter();
  const walkthroughState = useRecoilValue(walkthroughAtom);
  const pathnameMatches = (route: string) =>
    route === "/" ? route === router.pathname : router.pathname.includes(route);
  const SIDEBAR_ITEMS: SidebarItem[] = [
    {
      name: "Home",
      href: "/",
      target: "home-nav",
      icon: <DashboardTwoTone css={{ fontSize: 24 }} />,
    },
    {
      name: "Surveys",
      href: "/surveys",
      target: "surveys-nav",
      hidden: !hasSurveysPermission.view,
      icon: <ListAltTwoTone css={{ fontSize: 24 }} />,
    },
    {
      name: "Survey Insights",
      href: "/insights",
      target: "survey-insights-nav",
      hidden: !hasInsightsPermission.view,
      icon: <PieChartTwoTone css={{ fontSize: 24 }} />,
    },
    {
      name: "Team",
      href: "/team",
      target: "team-nav",
      hidden: !hasTeamPermission.view,
      icon: <RecentActorsTwoTone css={{ fontSize: 24 }} />,
    },
    {
      name: "Community Resources",
      href: "/community",
      target: "community-nav",
      hidden: !hasCommunityPermission.view,
      icon: <GroupTwoTone css={{ fontSize: 24 }} />,
    },
    {
      name: "Calendar",
      href: "/calendar",
      target: "calendar-nav",
      hidden: !hasCalendarPermission.view,
      icon: <CalendarTodayTwoTone css={{ fontSize: 24 }} />,
    },

    {
      name: "Surveys",
      href: "/view-surveys",
      target: "view-surveys-nav",
      icon: <PollTwoTone css={{ fontSize: 24 }} />,
      hidden: !isAdmin,
    },
  ];
  return (
    <div css={s.container}>
      <div css={s.top}>
        <img src={"/il_logo.svg"} alt={"inclusion labs logo"} />
        <div css={[s.top, { rowGap: "1.2rem" }]}>
          {SIDEBAR_ITEMS.filter((item) => !item.hidden).map(
            ({ name, target, href, icon, beta }) => (
              <button
                ref={walkthroughState[target]}
                data-cy={`${target}-button`}
                css={[
                  s.button,
                  pathnameMatches(href) && s.active,
                  disabled && {
                    ":hover": {
                      fontWeight: 400,
                    },
                  },
                ]}
                onClick={() => router.push(href)}
                key={href}
                disabled={disabled}
              >
                {icon}
                <p
                  css={[
                    disabled && {
                      color: colorVars.gray2,
                    },
                  ]}
                >
                  {name}
                </p>
                {beta && (
                  <p css={[s.beta, pathnameMatches(href) && s.betaActive]}>
                    BETA
                  </p>
                )}
              </button>
            )
          )}
        </div>
      </div>
      {!disabled && (
        <div css={[s.resources, pathnameMatches("/hub") && s.resourcesActive]}>
          <div css={s.resourceHeader}>
            <p
              css={[
                s.resourceTitle,
                pathnameMatches("/hub") && { color: "white" },
              ]}
            >
              Need help?
            </p>
            <Link href={"/hub"}>
              <button
                css={[
                  s.resourceButton,
                  pathnameMatches("/hub") && s.resourceButtonActive,
                ]}
              >
                Your DEI hub
              </button>
            </Link>
          </div>

          <img
            src={
              pathnameMatches("/hub")
                ? "/graphics/headphones_active.svg"
                : "/graphics/headphones.svg"
            }
            alt={"headphones graphic"}
            css={s.headphones}
          />
          <div css={[s.resourceLinks]}>
            <a
              onClick={() => {
                window.location.href =
                  "mailto:temi@inclusionlabs.co.uk?subject=Support Request";
              }}
              css={[s.link, pathnameMatches("/hub") && s.linkActive]}
            >
              <MailOutline css={{ fontSize: 16 }} />
              <p>Email us</p>
            </a>
            <a
              href={
                "https://calendly.com/inclusionlabs/contact-us-inclusion-labs-platform"
              }
              target={"_blank"}
              css={[s.link, pathnameMatches("/hub") && s.linkActive]}
            >
              <LocalPhoneOutlined css={{ fontSize: 16 }} />
              <p>Arrange a meeting</p>
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default SideBarNav;
